import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FormContainer from '../../components/FormContainer';
import Meta from '../../components/Meta';
import { useCheckDeliveryPartnerExistsMutation, useLoginDeliveryPartnerMutation } from '../../slices/usersApiSlice';
import { setCredentials } from '../../slices/authSlice';
import { auth } from '../../firebase';
import { signInWithPhoneNumber } from 'firebase/auth';
import { setupRecaptchaVerifier, clearRecaptcha } from '../../hooks/useRecaptchaVerifier';
import { toast } from 'react-toastify';
import initFirebaseAppCheck from '../../utils/initFirebaseAppCheck';
import DeliveryPartnerTermsModal from '../../components/DeliveryPartnerTermsModal';

const DeliveryPartnerLoginScreen = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const confirmationResultRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();  
  const [checkDeliveryPartnerExists] = useCheckDeliveryPartnerExistsMutation();
  const [login, { isLoading }] = useLoginDeliveryPartnerMutation();
  const { userInfo } = useSelector((state) => state.auth);

  const { search } = useLocation();
  const redirect = new URLSearchParams(search).get('redirect') || '/deliverypartner';

  const [showTerms, setShowTerms] = useState(false);

  if (process.env.NODE_ENV !== 'development'){
    initFirebaseAppCheck();
  }

  // Redirect if already logged in
  useEffect(() => {
    if (userInfo && userInfo.deliveryPartner?.isDeliveryPartner) {
      navigate(redirect);
    }
  }, [userInfo, redirect, navigate]);

  // Cleanup reCAPTCHA
  useEffect(() => {
    return () => {
      clearRecaptcha();
      confirmationResultRef.current = null;
    };
  }, []);

  const onSignInSubmit = async () => {
    const containerId = 'recaptcha-container';
    const formattedPhone = phone.startsWith('+') ? phone : `+91${phone}`;

    try {
      const { exists } = await checkDeliveryPartnerExists({ phone }).unwrap();
      if (!exists) {
        toast.error("Not an existing delivery partner. Please register below.", { autoClose: 2000 });
        return;
      }

      setSendingOtp(true);

      await new Promise((res) => requestAnimationFrame(res));

      const verifier = await setupRecaptchaVerifier(auth, containerId);
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      confirmationResultRef.current = confirmationResult;

      toast.success('OTP has been sent', { autoClose: 1000 });
    } catch (error) {
      console.error('Failed to send OTP:', error);
      toast.error('Could not send OTP. If new user, please register first. If existing user, please try again', {autoClose: 2000});
      setTimeout(() => window.location.reload(), 2000);
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    try {
      setVerifyingOtp(true);
      const confirmationResult = confirmationResultRef.current;
      if (!confirmationResult) {
        alert('OTP session expired. Please resend OTP.');
        return;
      }

      const result = await confirmationResult.confirm(otp);
      const token = await result.user.getIdToken(true);

      const res = await login(token).unwrap(); // backend verifies + sets session
      dispatch(setCredentials({ ...res })); // adjust if using deliveryPartner slice
      navigate(redirect);
    } catch (error) {
      console.error('OTP verification or login failed:', error);
      toast.error('Invalid OTP. Please try again.', { autoClose: 2000 });
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <FormContainer>
      <Meta />
      <h2>Delivery Partner Login</h2>
      <Form autoComplete="off">
        <Form.Group controlId="phone" className="my-3">
          <Form.Label>
            <strong>Phone <span className="text-danger">*</span></strong>
          </Form.Label>
          <Row>
            <Col>
              <Form.Control
                type="tel"
                placeholder="10 digit India phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </Col>
            <Col>
              <Button
                onClick={onSignInSubmit}
                disabled={isLoading || !phone || sendingOtp}
              >
                {sendingOtp ? (
                  <>
                    <Spinner animation="border" size="sm" /> Sending...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </Col>
          </Row>
        </Form.Group>
        <Form.Group>
          <Form.Label>
            <strong><span className="text-danger">Make sure to check for OTP in your Spam & Blocked SMS as well</span></strong>
          </Form.Label>
        </Form.Group>
        <Form.Group controlId="otp" className="my-3">
          <Form.Label>
            <strong>OTP <span className="text-danger">*</span></strong>
          </Form.Label>
          <Row>
            <Col>
              <Form.Control
                type="number"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </Col>
            <Col>
              <Button
                onClick={verifyOtp}
                disabled={isLoading || !otp || verifyingOtp}
              >
                {verifyingOtp ? (
                  <>
                    <Spinner animation="border" size="sm" /> Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </Button>
            </Col>
          </Row>
        </Form.Group>
      </Form>

      <div id="recaptcha-container" />
      
      <div className="mt-2">
        <p>
          By continuing to use the website, you agree to our{" "}
          <span
            className="terms-text-link"
            onClick={() => setShowTerms(true)}
            role="button"
            tabIndex={0}
          >
            Terms & Conditions
          </span>
        </p>
      </div>
      <DeliveryPartnerTermsModal show={showTerms} onHide={() => setShowTerms(false)} />

      <Row className="mt-2">
        <Col>
          New Delivery Partner?{' '}
          <Link to="/deliverypartner/register">Register</Link>
        </Col>
      </Row>
    </FormContainer>
  );
};

export default DeliveryPartnerLoginScreen;